import moment from "moment";

/**
 * @template T
 * 
 * @typedef { { recursive?: boolean, afterTransform?: (value: T) => T, input: T excludeKeys?: string[], omitKeys?: string[] } } DataTemplate
 */


/**
 * 
 * @template D
 * 
 * @param { DataTemplate<D> } data 
 * @property data.input - O valor a ser transformado, tem que ser do tipo Object ou Array
 * @property data.recursive? - Se o processamento é recursivo (Entrará em objetos e array aplicacará o processamento)
 * @property data.excludeKeys? - Propriedades que serão excluídas no processamento (não irá para o valor de saída)
 * @property data.omitKeys? - Propriedades que não terá tratamento (irão para o valor de saída sem processamento)
 * @property data.afterTransform? - Função que executará após o processamento
 * @returns { D }
 */
export const transformNullishToNullData = (data) => {

  if ((typeof data !== 'object' && data !== null) || !('input' in data)) 
    throw new Error("'transformNullishToNullData': data can be an object");
  
  const defaultParams = {
    recursive: true,
    afterTransform: null,
    input: null,
    excludeKeys: [],
    omitKeys: []
  }

  /** @type {typeof defaultParams} */
  const { afterTransform, input, recursive, excludeKeys, omitKeys } = Object
    .entries(defaultParams)
    .reduce((obj, [key, value]) => {
      obj[key] = data[key] || value;

      return obj;
    }, {});

  let result;
    
  if (Array.isArray(data.input)) {
    result = data.map((item) => String(item) == '[object Object]' && item !== null 
      ? transformNullishToNullData({ ...data, input: item }) 
      : item
    );
  } else {
    result = Object
      .keys(input)
      .reduce((obj, key) => {

        if (excludeKeys.includes(key)) return obj

        const value = input[key];

        if (omitKeys.includes(key)) {
          obj[key] = value;

          return obj;
        }

        if (recursive && Array.isArray(value)) {
          obj[key] = value.map(
            (item) => String(value) == '[object Object]' || Array.isArray(item)
              ? transformNullishToNullData({ ...data, input: item })
              : item
          );

          return obj;
        }

        if (recursive && String(value) == '[object Object]') {
          obj[key] = transformNullishToNullData({ ...data, input: value });

          return obj;
        }

        if (value == '0' || Number(value)) {
          // O '+' transforma a string em um número
          obj[key] = +value; 
          
          return obj;
        }

        obj[key] = value || null;

        return obj;
      }, {});
  }

  return afterTransform ? afterTransform(result) : result;
}

/**
 * Merges the properties of the source object into the target object, 
 * only if the property does not already exist in the target object.
 *
 * @param {object} target - The target object to merge properties into.
 * @param {object} source - The source object to merge properties from.
 * @return {object} The target object with merged properties.
 */
export const mergeDefault = (target, source) => {

  if (String(target) !== '[object Object]') {
    throw new Error('"mergeDefault": target must be an object');
  }

  if (String(source) !== '[object Object]') {
    throw new Error('"mergeDefault": target must be an object');
  }

  return Object
    .entries(source)
    .reduce((obj, [key, value]) => {
      obj[key] = target[key] || value;

      return obj;
    }, target);
}

/**
 * Converte uma string dada para o formato snake case.
 *
 * @param {string} str - a string a ser convertida para o formato snake case
 * @param {boolean} omitUnderline - indica se os underlines devem ser omitidos antes do tratamento (padrão é false)
 * @param {boolean} lowerCase - indica se palavra deve ser convertida para lowerCase após o tratamento (padrão é true)
 * @return {string} a string convertida para o formato snake case
 */
export const toSnakeCase = (str, omitUnderline = false, lowerCase = true) => {

  let search = str.replace(/([A-Z])/g, ':$&');
  
  if (omitUnderline) {
    search = search.replace(/_/g, '');
  }

  if (search.startsWith(':')) {
    search = search.slice(1);
  }

  const searchPieces = search.split(':').map((str) => lowerCase ? str.toLowerCase() : str).join('_');
  
  return searchPieces;
}

/** @param {string} str */
export const toCamelCase = (str) => str.replace(/([-_][a-z])/ig, ($1) => $1.toUpperCase().replace('-', '').replace('_', ''));

/** @param {Date} date */
export const toLaravelDateFormat = (date, throwIfInvalid = false) => {

  if (!Date.parse(date)) {
    if (throwIfInvalid) {
      throw new Error('toLaravelDateFormat: invalid date!');
    } else {
      return null;
    }
  }
  
  // -180 é para considerar o timezone de America/Sao_Paulo
  return moment(date).utc(-180).format("YYYY-MM-DD HH:mm:ss");
} 

/** 
 * Verifica se um objeto contém uma propriedade
 * @param {string} prop - nome da propriedade
 * @param {object | Array} data - origem, pode ser um Object ou Array (Caso contrário fará uma comparação se é igual)
 * @param {number|undefined} depth - profundidade (Caso queira que busque recursivamente dentro de uma array)
 * @default depth 0 (Não fará a procura recursivamente)
 * @returns {boolean} boolean
 */
export const hasIn = (prop, data, depth = 0) => {

  let currentDepth = 0;

  if (Array.isArray(data)) {
    return data.some((item) => currentDepth !== depth && Array.isArray(item) 
      ? hasIn(prop, item, depth) 
      : prop in item
    );
  }

  if (String(data) == '[object Object]') {
    return prop in data;
  }

  return prop == data;
}